/**
 * WGReferenceDocs Component - Working Group Reference Documents Management - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Comprehensive reference document management interface with search functionality, pagination,
 * and dual view modes (card/list). Features PDF modal integration with multi-modal support,
 * real-time search filtering, and paginated document browsing with responsive design.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of reference document objects
 * @param {string|number} props.data[].id - Unique document identifier
 * @param {string} props.data[].title - Document title
 * @param {string} props.data[].summary - Document summary/description
 * @param {string} props.data[].fileName - PDF file name/URL
 * @returns {JSX.Element} The WGReferenceDocs component
 * 
 * @example
 * <WGReferenceDocs data={referenceDocumentsArray} />
 */

import React, { useState, useMemo } from 'react';
import PDFModal from '../../modals/PDFModal';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import '../PolicyRegulatory/ConsultationCards.css'; // Import for consistent styling
import './WGReferenceDocsCards.css'; // Component-specific styles 


function WGReferenceDocs({ data }) {
  const [modals, setModals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const itemsPerPage = 6; // Number of items to show per page

  // Ensure data is always an array
  const safeData = data || [];

  /**
   * Cleans text content by removing markdown and formatting artifacts
   * 
   * @param {string} text - Raw text to clean
   * @returns {string} Cleaned text without formatting artifacts
   */
  const cleanText = (text) => {
    return text
      .replace(/\*\*/g, '')
      .replace(/\"\"/g, '')
      .replace(/\'\'/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  /**
   * Truncates text to a specified word limit with ellipsis
   * 
   * @param {string} text - Text to truncate
   * @param {number} wordLimit - Maximum number of words
   * @returns {string} Truncated text with ellipsis if needed
   */
  const truncateText = (text, wordLimit = 25) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  // Filter and paginate data based on search query
  const filteredData = useMemo(() => {
    /**
     * Filters documents based on search query matching title or summary
     * 
     * @returns {Array} Filtered array of documents matching search criteria
     */
    return safeData.filter(doc => 
      cleanText(doc.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      cleanText(doc.summary || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [safeData, searchQuery]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    /**
     * Creates paginated subset of filtered data for current page
     * 
     * @returns {Array} Array of documents for current page
     */
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  /**
   * Opens a PDF modal for document viewing
   * Manages modal stacking with z-index and positioning offsets
   * 
   * @param {string|number} id - Unique document identifier
   * @param {string} title - Document title for modal header
   * @param {string} pdfUrl - URL or filename for the PDF document
   */
  const openPDFModal = (id, title, pdfUrl) => {
    const existingModalIndex = modals.findIndex((modal) => modal.id === id);
    const maxZIndex = Math.max(...modals.map((modal) => modal.zIndex), 0) + 1;

    // Use ID-based URL instead of filename
    //const safeUrl = `${process.env.REACT_APP_API_URL}graph/getFileContentByListItemId/${id}`;

    if (existingModalIndex !== -1) {
      const updatedModals = [...modals];
      updatedModals[existingModalIndex].isOpen = true;
      updatedModals[existingModalIndex].zIndex = maxZIndex;
      setModals(updatedModals);
    } else {
      const newModal = {
        id,
        title,
        pdfUrl: pdfUrl, 
        isOpen: true,
        top: `${15 + (modals.length * 2)}%`,
        left: `${30 + (modals.length * 2)}%`,
        zIndex: maxZIndex,
      };
      setModals((prevModals) => [...prevModals, newModal]);
    }
  };

  /**
   * Closes a PDF modal by its unique identifier
   * 
   * @param {string|number} id - Unique identifier for the modal to close
   */
  const closePDFModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) =>
        modal.id === id ? { ...modal, isOpen: false } : modal
      )
    );
  };

  /**
   * Handles search input changes and resets pagination
   * 
   * @param {Event} e - Input change event
   */
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  /**
   * Handles pagination page changes
   * 
   * @param {number} page - Target page number
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
   * Highlights search terms in text
   * 
   * @param {string} text - Text to highlight
   * @param {string} highlight - Term to highlight
   * @returns {JSX.Element|string} Text with highlighted terms
   */
  const highlightSearchTerm = (text, highlight) => {
    if (!highlight.trim()) return text;
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? <mark key={index}>{part}</mark> : part
    );
  };

  return (
    <section className="wg-reference-docs-section">
      {/* Filter Controls and Status */}
      <div className="wg-filters-status">
        <div className="reference-docs-controls">
          {/* Search Box */}
          <div className="wg-search-box">
            <div className="search-input-container">
              <svg 
                aria-hidden="true" 
                focusable="false" 
                data-prefix="fas" 
                data-icon="magnifying-glass" 
                className="svg-inline--fa fa-magnifying-glass search-icon" 
                role="img" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 512 512"
              >
                <path 
                  fill="currentColor" 
                  d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={handleSearch}
                className="filtersearch"
              />
            </div>
          </div>

          {/* Stats Panel */}
          <div className="reference-docs-stats-panel">
            <span>
              {filteredData.length} document{filteredData.length !== 1 ? 's' : ''} found
              {searchQuery && (
                <span className="filter-indicator"> • Filtered from {safeData.length} total</span>
              )}
            </span>
          </div>

          {/* View Controls */}
          <div className="view-controls">
            <button  title="Card View"
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              <i className="fa-solid fa-table-cells"></i>
            </button>
            <button  title="List View"
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <i className="fa-solid fa-list"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {viewMode === 'cards' ? (
        <div className="consultation-cards-container">
          <div className="consultation-cards-grid">
            {paginatedData.map((doc) => (
              <Tippy 
                key={doc.id}
                content={doc.summary}
                placement="right"
                interactive={true}
                appendTo={() => document.body}
                theme="light"
                duration={200}
                delay={[200, 0]}
              >
                <div className="consultation-card">
                  <div 
                    className="consultation-card-content"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPDFModal(doc.id, doc.title, doc.fileName);
                    }}
                  >
                    <h3 className="consultation-card-title">{highlightSearchTerm(cleanText(doc.title), searchQuery)}</h3>
                    <p className="consultation-card-summary">{highlightSearchTerm(truncateText(cleanText(doc.summary)), searchQuery)}</p>
                  </div>
                  <div className="consultation-card-footer">
                    <div className="consultation-card-tags">
                      <span className="reference-document-tag">
                        <i className="fa-regular fa-file-pdf"></i>
                        Reference Document
                      </span>
                    </div>
                  </div>
                </div>
              </Tippy>
            ))}
          </div>
        </div>
      ) : (
        <div className="reference-docs-list">
          {paginatedData.map((doc) => (
            <div key={doc.id} className="reference-list-item">
              <div 
                className="reference-list-content"
                onClick={(e) => {
                  e.stopPropagation();
                  openPDFModal(doc.id, doc.title, doc.fileName);
                }}
              >
                <div className="reference-list-main">
                  <h3 className="reference-list-title">{highlightSearchTerm(cleanText(doc.title), searchQuery)}</h3>
                  <p className="reference-list-summary">{highlightSearchTerm(truncateText(cleanText(doc.summary), 35), searchQuery)}</p>
                </div>
                <div className="reference-list-footer">
                  <div className="reference-list-tags">
                    <span className="reference-document-tag">
                      <i className="fa-regular fa-file-pdf"></i>
                      Reference Document
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      )}

      {modals.map((modal) => (
        <PDFModal
          key={modal.id}
          title={modal.title}
          url={modal.pdfUrl}
          show={modal.isOpen}
          onClose={(e) => {
            e.stopPropagation();
            closePDFModal(modal.id);
          }}
          allowFullscreen={true}
          allowMove={true}
          showLinkinToolBar={true}
           />
      ))}
    </section>
  );
}

export default WGReferenceDocs;